import React from "react";
import { cn } from "../../lib/utils";
import { ShellHeader } from "../header/shell-header";

export function AppLayout({
  children,
  sidePanel,
  footer,
  headerProps = {},
  className = "",
  sidePanelClassName = "",
  contentClassName = "",
  footerClassName = "",
  sidePanelWidth = "w-80", // 320px default width
  showSidePanel = true,
}) {
  return (
    <div className="min-h-screen flex flex-col bg-page">
      {/* Shell Header - Fixed at top */}
      <ShellHeader
        {...headerProps}
        className={cn(
          "fixed top-0 left-0 right-0 z-50 bg-neutral-1 border-b border-neutral-5",
          headerProps.className
        )}
      />

      {/* Main Layout Container */}
      <div className="flex flex-1 pt-20"> {/* pt-20 accounts for fixed header height */}
        {/* Left Side Panel - Fixed/Sticky positioning */}
        {showSidePanel && (
          <aside
            className={cn(
              "fixed left-0 top-20 bottom-0 bg-neutral-2 border-r border-neutral-5 overflow-y-auto z-40",
              "transition-transform duration-300 ease-in-out",
              "lg:translate-x-0", // Always visible on large screens
              "max-lg:-translate-x-full", // Hidden on mobile by default
              sidePanelWidth,
              sidePanelClassName
            )}
          >
            <div className="p-6">
              {sidePanel}
            </div>
          </aside>
        )}

        {/* Main Content Area */}
        <main
          className={cn(
            "flex-1 bg-neutral-1 min-h-[calc(100vh-5rem)]", // 5rem = header height
            "transition-all duration-300 ease-in-out",
            showSidePanel && [
              "lg:ml-80", // Default left margin for side panel
              sidePanelWidth.replace("w-", "lg:ml-"), // Dynamic margin based on panel width
            ],
            contentClassName
          )}
        >
          <div className="p-6">
            {children}
          </div>
        </main>
      </div>

      {/* Fixed Footer */}
      {footer && (
        <footer
          className={cn(
            "bg-neutral-3 border-t border-neutral-5 px-6 py-4",
            "transition-all duration-300 ease-in-out",
            showSidePanel && [
              "lg:ml-80", // Match main content margin
              sidePanelWidth.replace("w-", "lg:ml-"),
            ],
            footerClassName
          )}
        >
          {footer}
        </footer>
      )}

      {/* Mobile Overlay - for side panel */}
      {showSidePanel && (
        <div className="lg:hidden fixed inset-0 bg-neutral-12/50 z-30 opacity-0 pointer-events-none transition-opacity duration-300" />
      )}
    </div>
  );
}

// Sidebar toggle hook for mobile responsiveness
export function useSidebarToggle() {
  const [isOpen, setIsOpen] = React.useState(false);

  const toggle = React.useCallback(() => {
    setIsOpen(prev => !prev);
  }, []);

  const close = React.useCallback(() => {
    setIsOpen(false);
  }, []);

  const open = React.useCallback(() => {
    setIsOpen(true);
  }, []);

  return { isOpen, toggle, close, open };
}

// Enhanced layout with mobile sidebar support
export function ResponsiveAppLayout({
  children,
  sidePanel,
  footer,
  headerProps = {},
  className = "",
  sidePanelClassName = "",
  contentClassName = "",
  footerClassName = "",
  sidePanelWidth = "w-80",
  showSidePanel = true,
}) {
  const { isOpen, toggle, close } = useSidebarToggle();

  // Close sidebar when clicking outside on mobile
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (isOpen && window.innerWidth < 1024) { // lg breakpoint
        const sidebar = document.getElementById('mobile-sidebar');
        if (sidebar && !sidebar.contains(event.target)) {
          close();
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, close]);

  return (
    <div className="min-h-screen flex flex-col bg-page">
      {/* Shell Header with mobile menu button */}
      <ShellHeader
        {...headerProps}
        className={cn(
          "fixed top-0 left-0 right-0 z-50 bg-neutral-1 border-b border-neutral-5",
          headerProps.className
        )}
        // Add mobile menu button if needed
        mobileMenuButton={showSidePanel && (
          <button
            onClick={toggle}
            className="lg:hidden p-2 rounded-md text-neutral-8 hover:bg-neutral-3 transition-colors"
            aria-label="Toggle sidebar"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        )}
      />

      {/* Main Layout Container */}
      <div className="flex flex-1 pt-20">
        {/* Left Side Panel */}
        {showSidePanel && (
          <aside
            id="mobile-sidebar"
            className={cn(
              "fixed left-0 top-20 bottom-0 bg-neutral-2 border-r border-neutral-5 overflow-y-auto z-40",
              "transition-transform duration-300 ease-in-out",
              "lg:translate-x-0", // Always visible on large screens
              isOpen ? "translate-x-0" : "-translate-x-full", // Mobile state
              sidePanelWidth,
              sidePanelClassName
            )}
          >
            <div className="p-6">
              {sidePanel}
            </div>
          </aside>
        )}

        {/* Main Content Area */}
        <main
          className={cn(
            "flex-1 bg-neutral-1 min-h-[calc(100vh-5rem)]",
            "transition-all duration-300 ease-in-out",
            showSidePanel && [
              "lg:ml-80", // Default left margin for side panel
              sidePanelWidth.replace("w-", "lg:ml-"),
            ],
            contentClassName
          )}
        >
          <div className="p-6">
            {children}
          </div>
        </main>
      </div>

      {/* Fixed Footer */}
      {footer && (
        <footer
          className={cn(
            "bg-neutral-3 border-t border-neutral-5 px-6 py-4",
            "transition-all duration-300 ease-in-out",
            showSidePanel && [
              "lg:ml-80",
              sidePanelWidth.replace("w-", "lg:ml-"),
            ],
            footerClassName
          )}
        >
          {footer}
        </footer>
      )}

      {/* Mobile Overlay */}
      {showSidePanel && isOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-neutral-12/50 z-30 transition-opacity duration-300"
          onClick={close}
        />
      )}
    </div>
  );
}
